import { DEFAULT_PLATFORM_PERCENT } from '@evi/shared';
import { formatFee, formatMoney, formatMoneyTenths } from './money';

/**
 * True-margin math for a home host, shared by the charger Settings form
 * and the earn walk (frictionless flow, piece 3) so the two can never
 * disagree about what a price is worth.
 *
 * The host receives 85% of the guest price — the settlement is a
 * destination charge to the home tenant's Connect account with EVI's
 * 15% platform fee carved out. So the honest margin is
 *   0.85 x guest price − electricity cost estimate
 * never guest price − cost, which would overstate by the fee. The
 * electricity estimate is the owner's informational Home Settings rate
 * (flat, or a min–max range for time-of-use / tiered) — display only,
 * never a charge.
 *
 * EVI's connection fee (Tier 2) is NOT in this sum. It is EVI's, set
 * per charger by EVI and kept whole: it rides on top of the guest price
 * and never comes out of the host's 85%, so the margin sentence names
 * it (whose it is) and the arithmetic ignores it.
 *
 * Tenth-of-a-cent precision: 85% of a whole-cent price lands on a half
 * cent (30¢ → 25.5¢), and rounding that to 26¢ would promise a host
 * money settlement never pays. The keep and the margin are therefore
 * integer TENTHS of a cent, printed with a third decimal only when it
 * is non-zero ($0.255, but $0.25).
 *
 * In the home's currency (item 3, 2026-10-02): hostMarginText takes the
 * currency and the app language and formats through money.ts, so a
 * Canadian host reads CA$0.255. The arithmetic is currency-free.
 *
 * Pure: takes cents, returns tenths. The caller renders the text through
 * the `myChargers` namespace's `detail.utilityRate.*` keys via
 * `hostMarginText`, so the wording lives in one place too.
 */

/**
 * The host's share of the guest price, in basis points: 10000 minus
 * EVI's platform share. Derived from @evi/shared so the web and the
 * settlement default cannot drift apart (85% at Tier 2).
 */
export const HOST_KEEP_BPS = 10_000 - DEFAULT_PLATFORM_PERCENT * 100;

export interface UtilityRateEstimate {
  type: 'flat' | 'time_of_use' | 'tiered';
  base_cents: number | null;
  min_cents: number | null;
  max_cents: number | null;
}

export type HostMargin =
  /** Guest price set, no electricity estimate to subtract. */
  | { kind: 'noRate'; guestCents: number; keepTenths: number }
  /** Flat estimate: one margin, positive or a loss. */
  | { kind: 'flat'; guestCents: number; keepTenths: number; costCents: number; marginTenths: number }
  /** TOU / tiered estimate: a margin range, either end may be negative. */
  | {
      kind: 'range';
      guestCents: number;
      keepTenths: number;
      minCostCents: number;
      maxCostCents: number;
      marginLowTenths: number;
      marginHighTenths: number;
      basis: 'time_of_use' | 'tiered';
    };

/** Whole cents the host banks on a gross amount (85%, after EVI's fee),
 *  computed the way settlement does: EVI's part is ROUNDED and the host
 *  keeps the rest, so a half cent goes to EVI (10¢ → 8¢, not 9¢) and the
 *  line never promises more than a payout pays. Used for the idle-fee keep
 *  line, which is quoted per minute. */
export function hostKeepCents(grossCents: number): number {
  return grossCents - Math.round((grossCents * (10_000 - HOST_KEEP_BPS)) / 10_000);
}

/** Tenths of a cent the host banks on a gross amount in cents: 30¢ →
 *  255 (25.5¢). Integer arithmetic, so no float drift in the third
 *  decimal. Floored: a per-kWh rate at odd cents (33¢ → 28.05¢) prints
 *  $0.280, never the $0.281 no session actually pays. */
export function hostKeepTenths(grossCents: number): number {
  return Math.floor((grossCents * HOST_KEEP_BPS) / 1_000);
}

/**
 * Parse a dollars-in-the-field string ("0.30", "0,30") to cents, or null
 * when blank / not a positive number. Comma decimals are normalised so a
 * localised numeric keypad cannot produce a NaN that reads as garbage.
 */
export function parseDollarsToCents(s: string): number | null {
  const trimmed = s.trim().replace(',', '.');
  if (trimmed === '') return null;
  const v = Number.parseFloat(trimmed);
  return Number.isFinite(v) && v > 0 ? Math.round(v * 100) : null;
}

export function computeHostMargin(
  guestCents: number | null,
  utilityRate: UtilityRateEstimate | null,
): HostMargin | null {
  if (guestCents == null || guestCents <= 0) return null;
  const keepTenths = hostKeepTenths(guestCents);
  if (!utilityRate || utilityRate.min_cents == null || utilityRate.max_cents == null) {
    return { kind: 'noRate', guestCents, keepTenths };
  }
  const lo = keepTenths - Math.round(utilityRate.max_cents * 10);
  const hi = keepTenths - Math.round(utilityRate.min_cents * 10);
  if (lo === hi) {
    return { kind: 'flat', guestCents, keepTenths, costCents: utilityRate.max_cents, marginTenths: lo };
  }
  return {
    kind: 'range',
    guestCents,
    keepTenths,
    minCostCents: utilityRate.min_cents,
    maxCostCents: utilityRate.max_cents,
    marginLowTenths: lo,
    marginHighTenths: hi,
    basis: utilityRate.type === 'time_of_use' ? 'time_of_use' : 'tiered',
  };
}

/** The currency and language the margin line is printed in. Absent:
 *  USD in English, what every caller passed before item 3. */
export interface MarginMoney {
  currency?: string | null;
  locale?: string | null;
}

/**
 * The margin sentence, from the `myChargers` namespace's
 * `detail.utilityRate.*` keys. `t` is that namespace's translator.
 * `loss` is true when a flat estimate exceeds the host's take.
 *
 * `connectionFeeCents` is the charger's EVI connection fee: above 0 the
 * lead sentence names it and says it goes to EVI; null, 0 or absent
 * leaves it out. The old variables (guest, keep, …) are still passed
 * beside `guestLine` so a locale not yet re-translated keeps rendering.
 *
 * `money` is the home's currency and the app language (item 3); every
 * amount in the sentence, the electricity estimate included, is in it.
 */
export function hostMarginText(
  m: HostMargin,
  t: (key: string, vars?: Record<string, string>) => string,
  connectionFeeCents?: number | null,
  money: MarginMoney = {},
): { text: string; loss: boolean } {
  const cents = (c: number) => formatMoney(c, money.currency, money.locale);
  const tenths = (x: number) => formatMoneyTenths(x, money.currency, money.locale);
  const signed = (x: number) => (x < 0 ? `−${tenths(x)}` : tenths(x));
  const guest = cents(m.guestCents);
  const keep = tenths(m.keepTenths);
  const fee = connectionFeeCents ?? 0;
  const guestLine = fee > 0
    ? t('detail.utilityRate.guestLineFee', { guest, fee: formatFee(fee, money.currency, money.locale) })
    : t('detail.utilityRate.guestLine', { guest });
  switch (m.kind) {
    case 'noRate':
      return { text: t('detail.utilityRate.marginNoRate', { guestLine, guest, keep }), loss: false };
    case 'flat': {
      const margin = tenths(m.marginTenths);
      const cost = cents(m.costCents);
      return m.marginTenths >= 0
        ? { text: t('detail.utilityRate.margin', { guestLine, guest, keep, cost, margin }), loss: false }
        : { text: t('detail.utilityRate.marginLoss', { guestLine, guest, keep, cost, margin }), loss: true };
    }
    case 'range':
      return {
        text: t('detail.utilityRate.marginRange', {
          guestLine,
          guest,
          keep,
          min: cents(m.minCostCents),
          max: cents(m.maxCostCents),
          marginLow: signed(m.marginLowTenths),
          marginHigh: signed(m.marginHighTenths),
          basis: m.basis === 'time_of_use'
            ? t('detail.utilityRate.basisTou')
            : t('detail.utilityRate.basisTiered'),
        }),
        loss: m.marginHighTenths < 0,
      };
  }
}
